import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  CircularProgress,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  TableSortLabel,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCompany } from './CompanyContext';
import FilterToolbar from './FilterToolbar';
import { formatDate } from './utils/dateUtils';
import { debounce } from 'lodash';

const API_URL = '/api/solicitudes_cotizacion';

const statusColors = {
  'Borrador': 'default',
  'Ganador': 'success',
  'Cancelado': '#000000',
  'Terminado': '#FFC107',
};

const RFQ_STATUSES = [
  { value: 'Borrador', label: 'Borrador' },
  { value: 'Ganador', label: 'Ganador' },
  { value: 'cancelada', label: 'Cancelada' },
  { value: 'terminada', label: 'Terminada' },
];

const getDefaultFilters = () => {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  return {
    q: '',
    startDate: thirtyDaysAgo.toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    statuses: ['Borrador', 'Ganador'], // Default active
  };
};

const highlightText = (text, query) => {
  if (!query || !text) {
    return text;
  }
  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return (
    <span>
      {parts.map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={index}>{part}</mark>
        ) : (
          part
        )
      )}
    </span>
  );
};

function SolicitudCotizacionList() {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(getDefaultFilters());
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCompanyIds } = useCompany();

  useEffect(() => {
    const fetchSolicitudes = debounce(async (currentFilters, companyIds, currentSortConfig) => {
      try {
        setLoading(true);
        const params = {
          empresa_ids: companyIds,
          q: currentFilters.q,
          start_date: currentFilters.startDate,
          end_date: (() => {
            if (!currentFilters.endDate) return '';
            const date = new Date(currentFilters.endDate);
            date.setDate(date.getDate() + 1);
            return date.toISOString().split('T')[0];
          })(),
          status: currentFilters.statuses,
          sort_by: currentSortConfig.key,
          sort_direction: currentSortConfig.direction,
        };
        const response = await axios.get(API_URL, {
          params,
          paramsSerializer: params => {
            return Object.entries(params)
              .flatMap(([key, value]) =>
                Array.isArray(value) ? value.map(v => `${key}=${encodeURIComponent(v)}`) : `${key}=${encodeURIComponent(value)}`
              )
              .join('&');
          }
        });
        setSolicitudes(response.data);
        setError('');
      } catch (error) {
        console.error('Error fetching solicitudes de cotizacion:', error);
        setError('No se pudieron cargar las solicitudes de cotización.');
      } finally {
        setLoading(false);
      }
    }, 500);

    if (location.state?.refresh) {
      fetchSolicitudes.cancel();
      fetchSolicitudes(filters, selectedCompanyIds, sortConfig);
      navigate(location.pathname, { replace: true, state: {} });
    } else {
      fetchSolicitudes(filters, selectedCompanyIds, sortConfig);
    }

    return () => {
      fetchSolicitudes.cancel();
    };
  }, [filters, selectedCompanyIds, sortConfig, location.state, navigate, location.pathname]);

  const handleRowClick = (id) => {
    navigate(`/solicitudes_cotizacion/${id}`);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Solicitudes de Cotización (RFQs)
        </Typography>
      </Box>

      <FilterToolbar
        filters={filters}
        onFilterChange={setFilters}
        statusOptions={RFQ_STATUSES}
      />

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <TableContainer component={Paper} elevation={3}>
          <Table sx={{ minWidth: 650 }} aria-label="solicitudes de cotizacion table">
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'doc_numb'}
                    direction={sortConfig.key === 'doc_numb' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('doc_numb')}
                  >
                    N° Cotización
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'proveedor'}
                    direction={sortConfig.key === 'proveedor' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('proveedor')}
                  >
                    Proveedor
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Artículos</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Empresa</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'estado'}
                    direction={sortConfig.key === 'estado' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('estado')}
                  >
                    Estado
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'created_at'}
                    direction={sortConfig.key === 'created_at' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('created_at')}
                  >
                    Fecha
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Última Nota</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {solicitudes.map((solicitud) => {
                const displayStatus = solicitud.status_ciclo && solicitud.status_ciclo !== 'activo'
                  ? solicitud.status_ciclo.charAt(0).toUpperCase() + solicitud.status_ciclo.slice(1)
                  : solicitud.estado;
                const chipColor = statusColors[displayStatus];
                const isCustomColor = chipColor && chipColor.startsWith('#');

                return (
                  <TableRow
                    key={solicitud.id}
                    hover
                    onClick={() => handleRowClick(solicitud.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {solicitud.doc_numb || 'Pendiente'}
                    </TableCell>
                    <TableCell>{highlightText(solicitud.proveedor ? solicitud.proveedor.nombre_fiscal : 'N/A', filters.q)}</TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          solicitud.detalles && solicitud.detalles.length > 0
                            ? solicitud.detalles.map(d => `${d.cantidad} x ${d.articulo}`).join(', ')
                            : 'Sin artículos'
                        }
                        arrow
                      >
                        <span>
                          {highlightText(
                            solicitud.detalles && solicitud.detalles.length > 0
                              ? solicitud.detalles[0].articulo
                              : 'Sin artículos',
                            filters.q
                          )}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{highlightText(solicitud.empresa ? solicitud.empresa.nombre : 'N/A', filters.q)}</TableCell>
                    <TableCell>
                      <Chip
                        label={displayStatus}
                        style={{ backgroundColor: isCustomColor ? chipColor : undefined, color: isCustomColor ? 'white' : undefined }}
                        color={!isCustomColor ? (chipColor || 'default') : undefined}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(solicitud.created_at)}</TableCell>
                    <TableCell>
                      <Tooltip title={solicitud.nota_preview || ''} arrow>
                        <span>{highlightText(solicitud.nota_preview || 'N/A', filters.q)}</span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}

export default SolicitudCotizacionList;
